/**
 * Session and permission context.
 *
 * TWO SEPARATE FACTS, FETCHED SEPARATELY:
 *
 *   · `GET /api/auth/me`          — who you are. Cheap, changes almost never.
 *   · `GET /api/permissions/mine` — what you may do. Changes the moment an admin edits a
 *                                   role, and drives every guard in the UI.
 *
 * `hasPermission` mirrors the server's `EffectivePermissions.check()` exactly, including
 * the SCOPE LADDER. `timesheets.approve.team` satisfies a request for `timesheets.approve`
 * (any scope will do) but not a request for `timesheets.approve.all`. Getting this wrong
 * in the client shows people buttons that 403 — annoying — or hides buttons they are
 * entitled to — worse, because it looks like the feature does not exist.
 *
 * The client-side check is CONVENIENCE, never enforcement. Every one of these permissions
 * is checked again by `requirePermission` on the route.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { api, isApiError, onSessionLost } from '@frontend/api/client';

export interface CurrentUser {
  id: string;
  email: string;
  displayName: string;
  firstName: string | null;
  lastName: string | null;
  roleIds: string[];
  employeeId: string | null;
  sessionId: string;
  isBreakGlass: boolean;
  mfaEnabled: boolean;
  mustChangePassword: boolean;
  authMethods: string[];
  timezone: string | null;
  locale: string | null;
  lastLoginAt: string | null;
}

/** The body of `GET /api/permissions/mine` — `EffectivePermissions.toJSON()`. */
export interface MyPermissions {
  userId: string;
  roleIds: string[];
  permissionSetIds: string[];
  isSuperAdmin: boolean;
  permissions: string[];
  groupedByModule: Record<string, string[]>;
  deniedPermissions: string[];
  resolvedAt: string;
}

/** Must match `permission-agent`'s ladder — wider scopes satisfy narrower requests. */
const SCOPE_RANK: Record<string, number> = {
  own: 1,
  team: 2,
  department: 3,
  costCenter: 3,
  all: 4,
};

const SCOPE_SUFFIXES = new Set(Object.keys(SCOPE_RANK));

function splitRequested(requested: string): { actionKey: string; minimumScope: string | null } {
  const lastDot = requested.lastIndexOf('.');
  if (lastDot === -1) return { actionKey: requested, minimumScope: null };
  const tail = requested.slice(lastDot + 1);
  if (SCOPE_SUFFIXES.has(tail)) return { actionKey: requested.slice(0, lastDot), minimumScope: tail };
  return { actionKey: requested, minimumScope: null };
}

/** Builds `actionKey -> widest held scope`, exactly as the resolver does server-side. */
function indexScopes(permissions: readonly string[]): Map<string, string> {
  const scopes = new Map<string, string>();
  for (const key of permissions) {
    const { actionKey, minimumScope } = splitRequested(key);
    // An unscoped grant behaves as the widest scope for that action.
    const scope = minimumScope ?? 'all';
    const current = scopes.get(actionKey);
    if (current === undefined || (SCOPE_RANK[scope] ?? 0) > (SCOPE_RANK[current] ?? 0)) {
      scopes.set(actionKey, scope);
    }
  }
  return scopes;
}

export interface AuthContextValue {
  status: 'LOADING' | 'AUTHENTICATED' | 'ANONYMOUS';
  user: CurrentUser | null;
  permissions: MyPermissions | null;
  /** True when the user holds the permission, honouring the scope ladder. */
  hasPermission: (permission: string) => boolean;
  hasAny: (permissions: readonly string[]) => boolean;
  hasAll: (permissions: readonly string[]) => boolean;
  /** The widest scope held for an action key, or `null`. Drives "you can see your team". */
  scopeFor: (actionKey: string) => string | null;
  /** Set when the session ended mid-session rather than never existing. */
  sessionExpired: boolean;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const queryClient = useQueryClient();
  const [sessionExpired, setSessionExpired] = useState(false);

  const meQuery = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.get<CurrentUser>('/auth/me'),
    retry: false,
    staleTime: 5 * 60_000,
  });

  const isSignedIn = meQuery.data !== undefined;

  const permissionsQuery = useQuery({
    queryKey: ['auth', 'permissions'],
    queryFn: () => api.get<MyPermissions>('/permissions/mine'),
    enabled: isSignedIn,
    retry: false,
    staleTime: 60_000,
  });

  useEffect(() => {
    const unsubscribe = onSessionLost((reason) => {
      if (reason === 'EXPIRED') setSessionExpired(true);
      queryClient.setQueryData(['auth', 'me'], undefined);
      queryClient.removeQueries({ queryKey: ['auth'] });
    });
    return () => {
      unsubscribe();
    };
  }, [queryClient]);

  const scopeIndex = useMemo(
    () => indexScopes(permissionsQuery.data?.permissions ?? []),
    [permissionsQuery.data],
  );
  const isSuperAdmin = permissionsQuery.data?.isSuperAdmin === true;

  const hasPermission = useCallback(
    (requested: string): boolean => {
      if (isSuperAdmin) return true;
      const { actionKey, minimumScope } = splitRequested(requested);
      const held = scopeIndex.get(actionKey);
      if (held === undefined) return false;
      if (minimumScope === null) return true;
      return (SCOPE_RANK[held] ?? 0) >= (SCOPE_RANK[minimumScope] ?? 0);
    },
    [isSuperAdmin, scopeIndex],
  );

  const hasAny = useCallback(
    (requested: readonly string[]) => requested.some((permission) => hasPermission(permission)),
    [hasPermission],
  );
  const hasAll = useCallback(
    (requested: readonly string[]) => requested.every((permission) => hasPermission(permission)),
    [hasPermission],
  );
  const scopeFor = useCallback(
    (actionKey: string): string | null => (isSuperAdmin ? 'all' : (scopeIndex.get(actionKey) ?? null)),
    [isSuperAdmin, scopeIndex],
  );

  const signOut = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // Signing out must always succeed locally, even if the server call fails.
    }
    api.signalSignedOut();
    setSessionExpired(false);
    queryClient.clear();
    window.location.assign('/login');
  }, [queryClient]);

  const refresh = useCallback(async () => {
    setSessionExpired(false);
    await queryClient.invalidateQueries({ queryKey: ['auth'] });
  }, [queryClient]);

  const status: AuthContextValue['status'] = meQuery.isLoading
    ? 'LOADING'
    : isSignedIn
      ? 'AUTHENTICATED'
      : 'ANONYMOUS';

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user: meQuery.data ?? null,
      permissions: permissionsQuery.data ?? null,
      hasPermission,
      hasAny,
      hasAll,
      scopeFor,
      sessionExpired,
      signOut,
      refresh,
    }),
    [
      status,
      meQuery.data,
      permissionsQuery.data,
      hasPermission,
      hasAny,
      hasAll,
      scopeFor,
      sessionExpired,
      signOut,
      refresh,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === null) throw new Error('useAuth must be used inside <AuthProvider>.');
  return context;
}

/** Shorthand for the common single-permission guard. */
export function usePermission(permission: string): boolean {
  return useAuth().hasPermission(permission);
}

/** True when the error means "your session ended", so callers can avoid a scary message. */
export function isSessionError(error: unknown): boolean {
  return isApiError(error) && error.kind === 'UNAUTHENTICATED';
}
